import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MessageCircle, Package, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { STORE, waLink, formatGs, sectorMeta } from "../data/store";
import { assetUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ShareButton, productPageUrl } from "./ShareButton";
import { FavoriteButton } from "./FavoriteButton";

export const ProductCard = ({ product, index = 0 }) => {
  const meta = sectorMeta(product.sector);
  const { add } = useCart();
  const msg = `Hola ${STORE.name}, consulto por el producto [Código: ${product.code}] ${product.name} de ${formatGs(product.price)}. ¿Está disponible?`;
  const inStock = product.stock > 0;

  const addToCart = () => {
    add(product);
    toast.success(`${product.name} agregado al pedido`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ duration: 0.35, delay: (index % 4) * 0.05 }}
      data-testid={`product-card-${product.code}`}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-[#0067B1] transition-[box-shadow,border-color] duration-200 flex flex-col overflow-hidden group"
    >
      <Link to={productPageUrl(product)} data-testid={`product-link-${product.code}`} className="relative aspect-[4/3] overflow-hidden bg-gray-100 block">
        <img
          src={assetUrl(product.image_url)} alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        <span
          className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-bold border"
          style={{ background: meta.bg, color: meta.text, borderColor: meta.border }}
        >
          {meta.label}
        </span>
        {product.featured && (
          <span className="absolute top-3 right-3 px-2 py-1 rounded-full text-[11px] font-bold bg-[#F59E0B] text-white">
            Destacado
          </span>
        )}
        <FavoriteButton product={product} className="absolute bottom-3 right-3 w-9 h-9 shadow-md" />
      </Link>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono-code text-xs font-bold text-gray-400">{product.code}</span>
          <span className="text-[11px] font-semibold text-gray-500">{product.brand}</span>
        </div>
        <h3 className="mt-1 font-display font-bold text-gray-900 leading-snug line-clamp-2">
          <Link to={productPageUrl(product)} className="hover:text-[#0067B1] transition-colors">{product.name}</Link>
        </h3>
        <p className="mt-1.5 text-sm text-gray-500 line-clamp-2 flex-1">{product.description}</p>

        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="text-gray-400">{product.unit}</span>
          <span className={`inline-flex items-center gap-1 font-semibold ${inStock ? "text-green-600" : "text-gray-400"}`}>
            <Package className="w-3.5 h-3.5" /> {inStock ? "Disponible" : "Sin stock"}
          </span>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100 flex items-end justify-between gap-2">
          <div className="font-display font-black text-xl tracking-tight text-[#E02424]">{formatGs(product.price)}</div>
        </div>

        <div className="mt-3 flex gap-2">
          <button
            data-testid={`add-to-cart-${product.code}`}
            onClick={addToCart}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#0A3977] text-white font-bold text-sm hover:bg-[#0067B1] transition-colors"
          >
            <ShoppingBag className="w-4 h-4" /> Agregar
          </button>
          <a
            data-testid={`whatsapp-inquiry-${product.code}`}
            href={waLink(msg)} target="_blank" rel="noreferrer" title="Consultar por WhatsApp"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#25D366] text-white hover:bg-[#1EBE5B] transition-colors shrink-0"
          >
            <MessageCircle className="w-4 h-4" />
          </a>
          <ShareButton product={product} />
        </div>
      </div>
    </motion.div>
  );
};
